import { _, api, helpers, ts } from '_core';
export async function loadAndCreateAnalyses<T extends string, TResource = any>(
  config: ts.types.analysis.AnalysisConfig<T, TResource>,
  resourceId: number,
  resourceType: ts.types.common.Resource['item_type'],
  isSharedResource: boolean,
  resource: TResource & { created_at: string }
): Promise<ts.types.analysis.AnalysisRef<T>[]> {
  try {
    const sort = (response: ts.types.analysis.AnalysisRef<T>[]) => {
      const grouped = _.groupBy(response, 'type');
      const sortedByConfig = config.analyses.flatMap((defaultAnalysis) => grouped[defaultAnalysis.type] || []);
      return sortedByConfig;
    };

    // Search for existing analyses
    const resp = await api.resource_analyses.search({
      ignore_user_scope: isSharedResource,
      query: ['$and', ['item_id_ref', '=', resourceId], ['item_type', '=', resourceType]],
      include: 'id,type,name',
    });

    const existingAnalyses = ((resp.data || []) as ts.types.analysis.ResourceAnalysis[]).map((a) => ({
      id: a.id,
      type: a.type as T,
      name: a.name,
    }));

    const existingTypes = existingAnalyses.map((a) => a.type);

    // Auto-create all missing analyses from config.analyses
    const allConfigTypes = config.analyses.map((a) => a.type);

    // Check if resource is new (just created) - if so, create all analyses, otherwise only missing ones
    let toCreate = allConfigTypes.filter((type) => !existingTypes.includes(type)) as T[];

    // Apply filter if available and resource is provided
    if (config.filterAnalyses && resource && toCreate.length > 0) {
      const filteredTypes = config.filterAnalyses(resource, toCreate);
      toCreate = toCreate.filter((type) => filteredTypes.includes(type));
    }

    // Create missing analyses only for new resources
    if (toCreate.length > 0 && _.isEmpty(existingAnalyses)) {
      const analysesToCreate = toCreate
        .map((type) => {
          const analysisConfig = config.analyses.find((a) => a.type === type);
          if (analysisConfig) {
            return {
              report_definition: analysisConfig.initialDefinition,
              item_id_ref: resourceId,
              item_type: resourceType,
              type,
              name: analysisConfig.name || type,
            };
          }
          return null;
        })
        .filter((analysis) => analysis !== null);

      if (analysesToCreate.length > 0) {
        const response = await api.combined.run({
          resource_analyses: {
            create: analysesToCreate,
          },
        });

        const createdAnalyses = (
          (response.resource_analyses?.create || []) as Array<{
            data: ts.types.analysis.ResourceAnalysis;
          }>
        ).map((item) => ({
          id: item.data.id,
          type: item.data.type as T,
          name: item.data.name,
        }));
        const allAnalyses = [...existingAnalyses, ...createdAnalyses];

        // Apply filter if available and resource is provided
        if (config.filterAnalyses && resource) {
          const filteredTypes = config.filterAnalyses(
            resource,
            allAnalyses.map((a) => a.type)
          );
          return sort(allAnalyses.filter((a) => filteredTypes.includes(a.type)));
        }
        return sort(allAnalyses);
      }
    }

    // Apply filter if available and resource is provided
    if (config.filterAnalyses && resource) {
      const filteredTypes = config.filterAnalyses(resource, existingTypes);
      return sort(existingAnalyses.filter((a) => filteredTypes.includes(a.type)));
    }
    return sort(existingAnalyses);
  } catch (error) {
    throw new Error(`Error loading/creating analyses - ${helpers.parseApiError(error as any)}`);
  }
}

export function validateAnalysisConfig<T extends string>(
  config: ts.types.analysis.AnalysisConfig<T>
): asserts config is ts.types.analysis.AnalysisConfig<T> {
  // Validate analyses array exists
  if (!config.analyses || !Array.isArray(config.analyses)) {
    throw new Error('AnalysisConfig validation failed: analyses must be an array');
  }

  if (config.analyses.length === 0) {
    throw new Error('AnalysisConfig validation failed: analyses array cannot be empty');
  }

  // Validate each analysis has required fields
  const requiredFields = ['type', 'name', 'initialDefinition', 'description'] as const;
  for (const analysis of config.analyses) {
    for (const field of requiredFields) {
      if (!(field in analysis) || analysis[field] === undefined || analysis[field] === null) {
        throw new Error(
          `AnalysisConfig validation failed: Missing required field '${field}' in analysis with type '${
            analysis.type || 'unknown'
          }'`
        );
      }
    }
  }

  // Validate all types are unique
  const types = config.analyses.map((a) => a.type);
  const uniqueTypes = new Set(types);
  if (types.length !== uniqueTypes.size) {
    const duplicates = types.filter((type, index) => types.indexOf(type) !== index);
    throw new Error(
      `AnalysisConfig validation failed: Duplicate analysis types found: ${Array.from(new Set(duplicates)).join(', ')}`
    );
  }
}
